import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { SpinnerService } from 'app/main/pages/spinner/spinner.service';
import { Subject, Observable } from 'rxjs';
import { takeUntil, mergeMap, map } from 'rxjs/operators';
import { EntityActionTypes, ofEntityType } from '@briebug/ngrx-auto-entity';

@Component({
    selector: 'app-add-entity',
    templateUrl: './add-entity.component.html',
    styleUrls: ['./add-entity.component.scss']
})
export class AddEntityComponent implements OnInit, OnDestroy {
    @Input() entityLabel: string = '';
    @Input() placeholder: string = '';
    @Input() model: any;
    @Input() existingEntities: any[] = [];
    @Input() entityType: any; // Type de l'entité (ex. Famille)
    @Input() entityNameField: string = 'name'; // Champ de l'entité utilisé pour le nom (ex. 'name')
    @Input() entityFacade: any; // Façade pour gérer les entités (ex. FamilleFacade)
    @Input() errorAddMessage: string = '';
    @Input() errorFailMessage: string = '';
    @Output() entityAdded = new EventEmitter<any>();

    inputControl = new FormControl('', Validators.required);
    private destroyed$ = new Subject<void>();

    constructor(
        private translateService: TranslateService,
        private messageService: MessageService,
        private spinnerService: SpinnerService
    ) {}

    ngOnInit(): void {
        // Initialiser le contrôle avec la valeur du modèle, si elle existe
        if (this.model) {
            this.inputControl.setValue(this.model[this.entityNameField]);
        }
    }

    addEntity(event: Event): void {
        event.stopPropagation();

        const inputValue = this.inputControl.value;
        if (this.model || !inputValue || typeof inputValue !== 'string') {
            return;
        }

        // Vérifier si l'entité existe déjà
        const entityExists = this.existingEntities.some(
            entity => entity[this.entityNameField].toLowerCase() === inputValue.toLowerCase()
        );
        if (entityExists) {
            this.translateService.get(["App", "ARTICLE"]).subscribe((msg) => {
                if (msg) {
                    this.messageService.add({
                        severity: "error",
                        summary: msg.App.SUMMARY_ERROR,
                        detail: msg[this.errorAddMessage],
                    });
                }
            });
            return;
        }

        // Créer une nouvelle entité
        const newEntity = new this.entityType();
        newEntity[this.entityNameField] = inputValue;

        this.spinnerService.show();
        this.entityFacade.create(newEntity);

        // Écouter le succès de la création
        this.entityFacade.actions$
            .pipe(
                ofEntityType(this.entityType, EntityActionTypes.CreateSuccess), // À adapter selon votre implémentation
                takeUntil(this.destroyed$),
                mergeMap(() => this.entityFacade.entities$.pipe(
                    takeUntil(this.destroyed$),
                    map(entities => Object.values(entities).find(
                        e => e[this.entityNameField] === newEntity[this.entityNameField]
                    ))
                ))
            )
            .subscribe(createdEntity => {
                if (createdEntity) {
                    this.model = createdEntity;
                    this.inputControl.setValue(createdEntity[this.entityNameField]);
                    this.spinnerService.hide();
                    this.entityAdded.emit(createdEntity);
                }
            });

        // Écouter l'échec de la création
        this.entityFacade.actions$
            .pipe(
                ofEntityType(this.entityType, EntityActionTypes.CreateFailure), // À adapter selon votre implémentation
                takeUntil(this.destroyed$)
            )
            .subscribe(() => {
                this.model = null;
                this.inputControl.setValue('');
                this.translateService.get(["App", "ARTICLE"]).subscribe((msg) => {
                    if (msg) {
                        this.messageService.add({
                            severity: "error",
                            summary: msg.App.SUMMARY_ERROR,
                            detail: msg[this.errorFailMessage],
                        });
                    }
                });
                this.spinnerService.hide();
            });
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }
}